import { ChevronRight, LayoutList, Search, X } from 'lucide-react';
import type { ReportDefinition } from '../../../../types/reports';
import { reportCategoryIcon } from '../reportIcons';

interface ReportLibraryProps {
  filteredReports: ReportDefinition[];
  reportCategories: string[];
  search: string;
  onSearchChange: (value: string) => void;
  categoryFilter: string;
  onCategoryFilterChange: (value: string) => void;
  selectedId: string | null;
  onSelect: (report: ReportDefinition) => void;
}

/** Left-hand catalog: search, category chips and the list of available reports. */
export function ReportLibrary({
  filteredReports,
  reportCategories,
  search,
  onSearchChange,
  categoryFilter,
  onCategoryFilterChange,
  selectedId,
  onSelect,
}: ReportLibraryProps) {
  return (
    <aside className="flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-sm xl:min-h-0">
      <div className="space-y-3 border-b border-border p-3.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <LayoutList size={15} />
            </span>
            <h3 className="text-sm font-semibold text-text">Available reports</h3>
          </div>
          <span className="rounded-full bg-background px-2 py-0.5 text-[0.6875rem] font-semibold tabular-nums text-text-muted">
            {filteredReports.length}
          </span>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search reports..."
            className="h-9 w-full rounded-lg border border-border bg-background pl-9 pr-8 text-sm text-text transition-colors placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted transition-colors hover:text-text"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {reportCategories.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {['all', ...reportCategories].map((category) => {
              const isActive = categoryFilter === category;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => onCategoryFilterChange(category)}
                  className={`shrink-0 rounded-full border px-2.5 py-1 text-[0.6875rem] font-medium transition-colors ${
                    isActive
                      ? 'border-primary bg-primary text-white shadow-sm shadow-primary/20'
                      : 'border-border bg-background text-text-muted hover:border-primary/40 hover:text-text'
                  }`}
                >
                  {category === 'all' ? 'All' : category}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="max-h-[65vh] divide-y divide-border/60 overflow-y-auto xl:max-h-none xl:min-h-0 xl:flex-1">
        {filteredReports.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-text-muted">
            <Search size={24} className="mx-auto mb-2 opacity-40" />
            No reports match your search.
          </div>
        ) : (
          filteredReports.map((report) => {
            const Icon = reportCategoryIcon(report.category);
            const isActive = selectedId === report.id;
            return (
              <button
                key={report.id}
                type="button"
                onClick={() => onSelect(report)}
                aria-current={isActive ? 'true' : undefined}
                className={`group relative block w-full px-3.5 py-3 text-left transition-colors ${
                  isActive ? 'bg-primary/[0.06]' : 'hover:bg-background/60'
                }`}
              >
                {isActive && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary" />}
                <div className="flex items-start gap-2.5">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 transition-colors ${
                      isActive
                        ? 'bg-primary text-white ring-primary/30'
                        : 'bg-background text-text-muted ring-border group-hover:text-primary'
                    }`}
                  >
                    <Icon size={15} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-medium leading-tight ${isActive ? 'text-primary' : 'text-text'}`}>
                        {report.name}
                      </p>
                      <ChevronRight
                        size={14}
                        className={`mt-0.5 shrink-0 transition-all ${
                          isActive
                            ? 'translate-x-0 text-primary'
                            : '-translate-x-0.5 text-text-muted/50 group-hover:translate-x-0 group-hover:text-text-muted'
                        }`}
                      />
                    </div>
                    <p className="mt-1 line-clamp-2 text-[0.6875rem] leading-snug text-text-muted">
                      {report.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}
